import { ColumnEditorOptions } from "primereact/column";
import { Question, isNumericQuestionType } from "../../types/checklist";
import {
    BooleanEditor,
    booleanDisplayTemplate,
    SingleChoiceEditor,
    singleChoiceDisplayTemplate,
    MultiChoiceEditor,
    multiChoiceDisplayTemplate,
    NumberEditor,
    numberDisplayTemplate,
    DateEditor,
    dateDisplayTemplate,
    TextEditor,
} from "./editors";

interface AnswerEditorProps {
    options: ColumnEditorOptions;
    question?: Question;
    fallbackType?: string | null;
    // Discrete types (boolean/single-choice) save on selection via this rather
    // than waiting for the cell to blur so the cascading questions appear instantly
    onCommit?: (value: unknown) => void;
}

/**
 * Dynamic answer editor that renders the appropriate input control
 * based on the question type from the checklist configuration.
 * Falls back to using the answer's own questionType when checklist is unavailable.
 */
export const AnswerEditor = ({ options, question, fallbackType, onCommit }: AnswerEditorProps) => {
    const questionType = question?.type || fallbackType;

    // If no question type available at all, fall back to text editor
    if (!questionType) {
        return <TextEditor options={options} />;
    }

    // Matched ahead of the switch so every numeric spelling gets the numeric
    // input. Matching only "number" here let a data-dictionary `float` fall
    // through to the default and accept free text in a m³ answer.
    if (isNumericQuestionType(questionType)) {
        return (
            <NumberEditor
                options={options}
                questionType={questionType}
                unit={question?.unit}
                min={question?.validation?.min}
                max={question?.validation?.max}
                decimalPlaces={question?.validation?.decimalPlaces}
            />
        );
    }

    switch (questionType) {
        case "boolean":
            return <BooleanEditor options={options} onCommit={onCommit} />;

        case "single-choice":
            if (!question?.options || question.options.length === 0) {
                return <TextEditor options={options} />;
            }
            return (
                <SingleChoiceEditor
                    options={options}
                    questionOptions={question.options}
                    onCommit={onCommit}
                />
            );

        case "multi-choice":
            if (!question?.options || question.options.length === 0) {
                return <TextEditor options={options} />;
            }
            return (
                <MultiChoiceEditor
                    options={options}
                    questionOptions={question.options}
                />
            );

        case "date":
            return <DateEditor options={options} />;

        case "text":
            return (
                <TextEditor
                    options={options}
                    maxLength={question?.validation?.maxLength}
                />
            );

        case "table":
            // Table type is complex, fall back to text for now
            return <TextEditor options={options} />;

        default:
            return <TextEditor options={options} />;
    }
};

/**
 * Get the display value for an answer based on question type.
 * Converts internal values (option IDs, booleans) to human-readable text.
 *
 * Resolves the type the same way AnswerEditor does, falling back to the answer's
 * own questionType when the checklist is unavailable. Without the fallback a
 * cell showed the raw stored value while its editor formatted the same answer —
 * a date read as YYYY-MM-DD until the picker opened, then reverted on close.
 */
export const getAnswerDisplayValue = (
    value: unknown,
    question?: Question,
    fallbackType?: string | null
): string => {
    if (value === null || value === undefined) return "";

    const questionType = question?.type || fallbackType;
    if (!questionType) {
        return String(value);
    }

    if (isNumericQuestionType(questionType)) {
        return numberDisplayTemplate(value);
    }

    switch (questionType) {
        case "boolean":
            return booleanDisplayTemplate(value);

        case "single-choice":
            return singleChoiceDisplayTemplate(value, question?.options);

        case "multi-choice":
            return multiChoiceDisplayTemplate(value, question?.options);

        case "date":
            return dateDisplayTemplate(value);

        default:
            return String(value);
    }
};
